/**
 * /checkin/signin — mobile-first sign-in for returning members.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  CheckInShell,
  CheckInLogo,
  PrimaryButton,
  TextField,
  CHECKIN_PALETTE,
} from "./_shell";
import { checkinApi, setToken, setCachedUser } from "@/lib/checkinApi";

export default function CheckInSignIn() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const out = await checkinApi.login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setToken(out.token);
      setCachedUser(out.user);
      const role = (out.user && (out.user.role || out.user.admin_role)) || "";
      // Route founding admins to the console — never drop them into
      // the customer dashboard even if they used the member sign-in.
      if (role === "checkin_admin" || role === "super_admin") {
        navigate("/checkin/admin", { replace: true });
      } else if (!out.user.onboarding_complete) {
        // If onboarding was never completed, resume it. Otherwise → home.
        navigate("/checkin/onboarding", { replace: true });
      } else {
        navigate("/checkin/home", { replace: true });
      }
    } catch (e2) {
      setErr(e2.message || "We couldn't sign you in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CheckInShell testid="checkin-signin">
      <div className="pt-2 flex items-center justify-between">
        <CheckInLogo small />
        <Link to="/checkin" className="text-xs" style={{ color: CHECKIN_PALETTE.purple }}
              data-testid="signin-back">
          ← Back
        </Link>
      </div>

      <div className="mt-8">
        <h1 className="font-serif text-3xl leading-tight"
            style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Welcome back.
        </h1>
        <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
          Pick up where you left off with Dr. C.
        </p>
      </div>

      <form className="mt-6 space-y-4" onSubmit={onSubmit} data-testid="signin-form">
        <TextField
          label="Email" testid="signin-email" required type="email"
          autoComplete="email" inputMode="email" value={form.email}
          onChange={(e) => up("email", e.target.value)}
          placeholder="you@example.com"
        />
        <TextField
          label="Password" testid="signin-password" required
          type="password" autoComplete="current-password"
          value={form.password} onChange={(e) => up("password", e.target.value)}
          placeholder="Your password"
        />

        {err ? (
          <div className="rounded-2xl border px-4 py-3 text-sm"
               style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}
               data-testid="signin-error">
            {err}
          </div>
        ) : null}

        <div className="pt-2">
          <PrimaryButton type="submit" disabled={submitting} testid="signin-submit">
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
              </span>
            ) : "Sign in"}
          </PrimaryButton>
        </div>

        <p className="text-center text-[12px] pt-2" style={{ color: "#6C6478" }}>
          New here?{" "}
          <Link to="/checkin/register" className="underline"
                style={{ color: CHECKIN_PALETTE.purple }} data-testid="signin-register-link">
            Start your check-in
          </Link>
        </p>
      </form>
    </CheckInShell>
  );
}
